import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  UserCog,
  Building,
  RefreshCw,
  Settings,
  ChevronDown,
  ChevronRight,
  Search,
  Eye,
  Check,
  Sparkles,
  Lock,
  ArrowLeft,
  BookOpen,
  Calendar,
  Edit3,
  User,
  Mail
} from 'lucide-react';
import { db, collection, doc, setDoc, deleteDoc, getDocs, getDoc, updateDoc, addDoc } from '../../firebase';
import styles from '../CSS/GroupManagement.module.css';

const GroupManagement = ({
  users = [],
  roles = [],
  availablePermissions = [],
  organizationalUnits = [],
  onUpdate,
  showFeedback,
  currentUser
}) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOUs, setExpandedOUs] = useState(new Set());
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'detail'
  const [selectedGroupDetail, setSelectedGroupDetail] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [availableUsersToAdd, setAvailableUsersToAdd] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organizationalUnits: [],
    roles: [],
    customPermissions: {}
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groupsList = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsList);
    } catch (error) {
      console.error('Error cargando grupos:', error);
      showFeedback('error', 'Error al cargar grupos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      organizationalUnits: [],
      roles: [],
      customPermissions: {}
    });
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      organizationalUnits: group.organizationalUnits || [],
      roles: group.roles || [],
      customPermissions: group.customPermissions || {}
    });
    setShowEditModal(true);
  };

  const handleOpenDeleteModal = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const handleViewGroupDetail = (group) => {
    setSelectedGroupDetail(group);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedGroupDetail(null);
    setExpandedUsers({});
    setUserSearchTerm('');
  };

  const handleOpenAddUserModal = () => {
    // Obtener usuarios que NO están en el grupo (por OU) para añadirlos manualmente
    const groupUsers = getUsersInGroup(selectedGroupDetail);
    const groupUserEmails = new Set(groupUsers.map(u => u.id));

    const available = users.filter(u => !groupUserEmails.has(u.id));
    setAvailableUsersToAdd(available);
    setShowAddUserModal(true);
  };

  const handleAddUserToGroup = async (userEmail) => {
    try {
      const groupRef = doc(db, 'groups', selectedGroupDetail.id);
      const groupDoc = await getDoc(groupRef);

      if (!groupDoc.exists()) {
        showFeedback('error', 'Grupo no encontrado');
        return;
      }

      const groupData = groupDoc.data();
      const customPermissions = groupData.customPermissions || {};

      // Si el usuario ya tiene permisos custom, no hacer nada
      if (customPermissions[userEmail]) {
        showFeedback('warning', 'El usuario ya tiene permisos personalizados en este grupo');
        return;
      }

      // Añadir usuario con permisos vacíos (se configurarán después)
      customPermissions[userEmail] = {
        permissions: [],
        emailPermissions: { allowedOUs: [] },
        classPermissions: { allowedGroups: [] },
        reservationPermissions: { advancedPermissions: [] }
      };

      await updateDoc(groupRef, { customPermissions });

      showFeedback('success', 'Usuario añadido al grupo correctamente');
      setShowAddUserModal(false);
      await fetchGroups();

      // Refrescar la vista del grupo actual
      const updatedGroupDoc = await getDoc(groupRef);
      if (updatedGroupDoc.exists()) {
        setSelectedGroupDetail({
          id: updatedGroupDoc.id,
          ...updatedGroupDoc.data()
        });
      }
    } catch (error) {
      console.error('Error añadiendo usuario:', error);
      showFeedback('error', 'Error al añadir usuario: ' + error.message);
    }
  };

  const handleRemoveUserFromGroup = async (userEmail) => {
    try {
      const groupRef = doc(db, 'groups', selectedGroupDetail.id);
      const groupDoc = await getDoc(groupRef);

      if (!groupDoc.exists()) {
        showFeedback('error', 'Grupo no encontrado');
        return;
      }

      const groupData = groupDoc.data();
      const customPermissions = { ...groupData.customPermissions } || {};

      // Eliminar usuario de permisos custom
      delete customPermissions[userEmail];

      await updateDoc(groupRef, { customPermissions });

      showFeedback('success', 'Usuario eliminado del grupo correctamente');
      await fetchGroups();

      // Refrescar la vista del grupo actual
      const updatedGroupDoc = await getDoc(groupRef);
      if (updatedGroupDoc.exists()) {
        setSelectedGroupDetail({
          id: updatedGroupDoc.id,
          ...updatedGroupDoc.data()
        });
      }
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      showFeedback('error', 'Error al eliminar usuario: ' + error.message);
    }
  };

  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };


  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showFeedback('error', 'El nombre del grupo es requerido');
      return;
    }

    if (formData.organizationalUnits.length === 0 && formData.roles.length === 0) {
      showFeedback('error', 'Debe seleccionar al menos una OU o un rol');
      return;
    }

    setLoading(true);
    try {
      const groupData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        organizationalUnits: formData.organizationalUnits,
        roles: formData.roles,
        customPermissions: formData.customPermissions,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.email || 'unknown',
        ...(selectedGroup ? {} : { createdAt: new Date().toISOString() })
      };

      if (selectedGroup) {
        // Actualizar grupo existente
        const groupRef = doc(db, 'groups', selectedGroup.id);
        await updateDoc(groupRef, groupData);
      } else {
        // Crear nuevo grupo con ID autogenerado por Firestore
        await addDoc(collection(db, 'groups'), groupData);
      }

      showFeedback('success', `Grupo ${selectedGroup ? 'actualizado' : 'creado'} exitosamente`);
      setShowCreateModal(false);
      setShowEditModal(false);
      fetchGroups();
      onUpdate?.();
    } catch (error) {
      console.error('Error guardando grupo:', error);
      showFeedback('error', 'Error al guardar el grupo');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'groups', selectedGroup.id));
      showFeedback('success', 'Grupo eliminado exitosamente');
      setShowDeleteModal(false);
      fetchGroups();
      onUpdate?.();
    } catch (error) {
      console.error('Error eliminando grupo:', error);
      showFeedback('error', 'Error al eliminar el grupo');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(id => id !== roleId)
        : [...prev.roles, roleId]
    }));
  };

  const toggleOU = (ouPath) => {
    setFormData(prev => ({
      ...prev,
      organizationalUnits: prev.organizationalUnits.includes(ouPath)
        ? prev.organizationalUnits.filter(path => path !== ouPath)
        : [...prev.organizationalUnits, ouPath]
    }));
  };

  const toggleExpandOU = (ouPath) => {
    const newExpanded = new Set(expandedOUs);
    if (newExpanded.has(ouPath)) {
      newExpanded.delete(ouPath);
    } else {
      newExpanded.add(ouPath);
    }
    setExpandedOUs(newExpanded);
  };

  const getGroupTotalPermissions = (group) => {
    const permissionsSet = new Set();
    group.roles?.forEach(roleId => {
      const role = roles.find(r => r.id === roleId);
      role?.permissions?.forEach(p => permissionsSet.add(p));
    });
    return Array.from(permissionsSet);
  };

  const getUsersInGroup = (group) => {
    console.log('🔍 getUsersInGroup llamado para:', group.name);
    console.log('📋 Total usuarios disponibles:', users.length);
    console.log('🏢 OUs del grupo:', group.organizationalUnits);

    // Obtener usuarios con permisos custom (añadidos manualmente desde BD)
    const usersWithCustomPerms = new Set();
    if (group.customPermissions) {
      Object.keys(group.customPermissions).forEach(email => {
        usersWithCustomPerms.add(email);
      });
    }
    console.log('👤 Usuarios con permisos custom:', usersWithCustomPerms.size);

    // Si no hay OUs seleccionadas, solo mostrar usuarios con permisos custom
    if (!group.organizationalUnits || group.organizationalUnits.length === 0) {
      console.log('⚠️ Grupo sin OUs - mostrando solo usuarios con permisos custom');
      const customUsers = users.filter(u => usersWithCustomPerms.has(u.id));
      console.log(`👥 RESULTADO: ${customUsers.length} usuarios custom`);
      return customUsers;
    }

    // ✅ Obtener usuarios por OU
    const usersWithOU = users.filter(u => u.orgUnit || u.orgUnitPath);
    console.log(`📊 Usuarios con orgUnit: ${usersWithOU.length}/${users.length}`);

    const usersInOU = users.filter(user => {
      // ✅ ARREGLADO: Soportar tanto 'orgUnit' como 'orgUnitPath' (legacy)
      const userOU = user.orgUnit || user.orgUnitPath;

      if (!userOU) {
        return false;
      }

      // Verificar si la OU del usuario está en la lista de OUs del grupo
      // o si es una sub-OU de alguna OU del grupo
      const matches = group.organizationalUnits.some(groupOU => {
        // Coincidencia exacta
        if (userOU === groupOU) {
          return true;
        }

        // Verificar si es una sub-OU
        if (userOU.startsWith(groupOU + '/')) {
          return true;
        }

        return false;
      });

      return matches;
    });

    // ✅ COMBINAR: Usuarios por OU + Usuarios con permisos custom
    const allUserEmails = new Set([
      ...usersInOU.map(u => u.id),
      ...Array.from(usersWithCustomPerms)
    ]);

    const allUsers = users.filter(u => allUserEmails.has(u.id));

    console.log(`👥 RESULTADO: ${allUsers.length} usuarios en grupo "${group.name}"`);
    console.log(`   ├─ Por OU: ${usersInOU.length}`);
    console.log(`   └─ Custom: ${usersWithCustomPerms.size}`);

    return allUsers;
  };

  const getUsersWithCustomPermissions = (group) => {
    if (!group.customPermissions) return [];
    return Object.entries(group.customPermissions)
      .filter(([_, data]) => {
        if (typeof data === 'object' && data.permissions) {
          return data.permissions.length > 0 ||
                 data.emailPermissions?.allowedOUs?.length > 0 ||
                 data.classPermissions?.allowedGroups?.length > 0 ||
                 data.reservationPermissions?.advancedPermissions?.length > 0;
        }
        return data && data.length > 0; // Legacy format
      })
      .map(([userId, data]) => ({
        userId,
        user: users.find(u => u.id === userId),
        permissions: typeof data === 'object' && data.permissions ? data.permissions : data
      }));
  };

  const getPermissionColor = (permissionId) => {
    const colors = {
      'gestion': '#8b5cf6',
      'correos': '#f59e0b',
      'clases': '#10b981',
      'reservas': '#3b82f6',
      'incidencias': '#ef4444',
      'admin': '#eab308'
    };
    return colors[permissionId] || '#6b7280';
  };

  const getPermissionIcon = (permissionId) => {
    const icons = {
      'gestion': '⚙️',
      'correos': '📧',
      'clases': '📚',
      'reservas': '📅',
      'incidencias': '🎫',
      'admin': '👑'
    };
    return icons[permissionId] || '🔐';
  };

  const updateUserPermissions = async (userId, permissions, advancedPermissions = {}) => {
    if (!selectedGroupDetail) return;

    setLoading(true);
    try {
      const groupDoc = doc(db, 'groups', selectedGroupDetail.id);
      const newCustomPermissions = { ...selectedGroupDetail.customPermissions };

      if (permissions.length > 0 || Object.keys(advancedPermissions).length > 0) {
        newCustomPermissions[userId] = {
          permissions: permissions,
          ...advancedPermissions
        };
      } else {
        delete newCustomPermissions[userId];
      }

      await setDoc(groupDoc, {
        customPermissions: newCustomPermissions,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.email || 'unknown'
      }, { merge: true });

      setSelectedGroupDetail({
        ...selectedGroupDetail,
        customPermissions: newCustomPermissions
      });

      showFeedback('success', 'Permisos actualizados exitosamente');
      fetchGroups();
    } catch (error) {
      console.error('Error actualizando permisos:', error);
      showFeedback('error', 'Error al actualizar permisos');
    } finally {
      setLoading(false);
    }
  };

  const renderOUTree = (ous, level = 0) => {
    return ous.map(ou => {
      const isExpanded = expandedOUs.has(ou.orgUnitPath);
      const isSelected = formData.organizationalUnits.includes(ou.orgUnitPath);
      const hasChildren = ou.children && ou.children.length > 0;

      return (
        <div key={ou.orgUnitPath} className={styles.ouTreeItem}>
          <div
            className={styles.ouItemRow}
            style={{ paddingLeft: `${level * 20}px` }}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggleExpandOU(ou.orgUnitPath)}
                className={styles.ouExpandButton}
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : (
              <span className={styles.ouExpandSpacer}></span>
            )}

            <button
              type="button"
              onClick={() => toggleOU(ou.orgUnitPath)}
              className={`${styles.ouSelectButton} ${isSelected ? styles.ouSelected : ''}`}
            >
              <div className={styles.ouCheckbox}>
                {isSelected && <Check size={12} />}
              </div>

              <Building size={14} className={styles.ouIcon} />

              <div className={styles.ouInfo}>
                <span className={styles.ouName}>{ou.name}</span>
                <span className={styles.ouPath}>{ou.orgUnitPath}</span>
              </div>

              {ou.userCount > 0 && (
                <span className={styles.ouUserCount}>
                  {ou.userCount}
                </span>
              )}
            </button>
          </div>

          {isExpanded && hasChildren && (
            <div className={styles.ouChildren}>
              {renderOUTree(ou.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderFormModal = (isEdit = false) => (
    <div className={styles.modalOverlay} onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {isEdit ? 'Editar Grupo' : 'Crear Nuevo Grupo'}
          </h3>
          <button
            className={styles.closeButton}
            onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Información Básica */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Nombre del Grupo</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="ej: Profesores de Matemáticas"
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Descripción</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción del grupo..."
              className={styles.textarea}
              rows={2}
            />
          </div>

          {/* Unidades Organizativas */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Building size={16} />
              <span>Unidades Organizativas</span>
            </div>

            {organizationalUnits.length === 0 ? (
              <div className={styles.emptyBox}>
                <Building size={20} />
                <span>No hay unidades organizativas</span>
              </div>
            ) : (
              <div className={styles.ouTreeContainer}>
                {renderOUTree(organizationalUnits)}
              </div>
            )}
          </div>

          {/* Roles del Sistema */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Shield size={16} />
              <span>Roles del Sistema</span>
            </div>

            <div className={styles.rolesGrid}>
              {roles.map(role => {
                const isSelected = formData.roles.includes(role.id);
                const permissionCount = role.permissions?.length || 0;

                return (
                  <button
                    key={role.id}
                    type="button"
                    className={`${styles.roleCard} ${isSelected ? styles.roleCardSelected : ''}`}
                    onClick={() => toggleRole(role.id)}
                  >
                    <div className={styles.roleCheckbox}>
                      {isSelected && <Check size={12} />}
                    </div>
                    <div className={styles.roleInfo}>
                      <span className={styles.roleName}>{role.name}</span>
                      <span className={styles.roleMeta}>{permissionCount} permisos</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
            className={`${styles.button} ${styles.secondaryButton}`}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className={`${styles.button} ${styles.primaryButton}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <RefreshCw size={16} className={styles.spinning} />
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                {isEdit ? 'Actualizar' : 'Crear'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Vista de lista de grupos
  const renderGroupsList = () => (
    <>
      <div className={styles.toolbar}>
        <div className={styles.searchBar}>
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar grupos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <button className={styles.createButton} onClick={handleOpenCreateModal}>
          <Plus size={18} />
          Nuevo Grupo
        </button>
      </div>

      <div className={styles.groupsList}>
        {loading && groups.length === 0 ? (
          <div className={styles.loadingState}>
            <RefreshCw size={32} className={styles.spinning} />
            <p>Cargando grupos...</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className={styles.emptyState}>
            <Users size={48} />
            <h3>No hay grupos</h3>
            <p>{searchTerm ? 'No se encontraron grupos' : 'Crea tu primer grupo'}</p>
            {!searchTerm && (
              <button className={styles.createButton} onClick={handleOpenCreateModal}>
                <Plus size={18} />
                Crear Grupo
              </button>
            )}
          </div>
        ) : (
          filteredGroups.map(group => {
            const totalPermissions = getGroupTotalPermissions(group);
            const usersWithCustom = getUsersWithCustomPermissions(group);
            const ouCount = group.organizationalUnits?.length || 0;
            const roleCount = group.roles?.length || 0;

            return (
              <div key={group.id} className={styles.groupCard}>
                <div className={styles.groupCardContent}>
                  <div className={styles.groupCardMain}>
                    <h3 className={styles.groupCardTitle}>{group.name}</h3>
                    {group.description && (
                      <p className={styles.groupCardDescription}>{group.description}</p>
                    )}
                  </div>

                  <div className={styles.groupCardStats}>
                    {ouCount > 0 && (
                      <div className={`${styles.stat} ${styles.statBlue}`} title={`${ouCount} unidad${ouCount !== 1 ? 'es' : ''} organizativa${ouCount !== 1 ? 's' : ''}`}>
                        <Building size={14} />
                        <span className={styles.statLabel}>OUs:</span>
                        <span className={styles.statValue}>{ouCount}</span>
                      </div>
                    )}
                    {roleCount > 0 && (
                      <div className={`${styles.stat} ${styles.statPurple}`} title={`${roleCount} rol${roleCount !== 1 ? 'es' : ''} asignado${roleCount !== 1 ? 's' : ''}`}>
                        <Shield size={14} />
                        <span className={styles.statLabel}>Roles:</span>
                        <span className={styles.statValue}>{roleCount}</span>
                      </div>
                    )}
                    <div className={`${styles.stat} ${styles.statGreen}`} title={`${totalPermissions.length} permiso${totalPermissions.length !== 1 ? 's' : ''} total${totalPermissions.length !== 1 ? 'es' : ''}`}>
                      <Settings size={14} />
                      <span className={styles.statLabel}>Permisos:</span>
                      <span className={styles.statValue}>{totalPermissions.length}</span>
                    </div>
                    {usersWithCustom.length > 0 && (
                      <div className={`${styles.stat} ${styles.statOrange}`} title={`${usersWithCustom.length} usuario${usersWithCustom.length !== 1 ? 's' : ''} con permisos personalizados`}>
                        <Sparkles size={14} />
                        <span className={styles.statLabel}>Custom:</span>
                        <span className={styles.statValue}>{usersWithCustom.length}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.groupCardActions}>
                  <button
                    className={styles.iconButton}
                    onClick={() => handleViewGroupDetail(group)}
                    title="Ver usuarios"
                  >
                    <Users size={16} />
                  </button>
                  <button
                    className={styles.iconButton}
                    onClick={() => handleOpenEditModal(group)}
                    title="Editar"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className={`${styles.iconButton} ${styles.danger}`}
                    onClick={() => handleOpenDeleteModal(group)}
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  // Vista de detalle del grupo con gestión de usuarios
  const renderGroupDetail = () => {
    if (!selectedGroupDetail) return null;

    const groupUsers = getUsersInGroup(selectedGroupDetail);
    const totalPermissions = getGroupTotalPermissions(selectedGroupDetail);

    const filteredUsers = groupUsers.filter(user => {
      if (!userSearchTerm) return true;
      const searchLower = userSearchTerm.toLowerCase();
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = user.id.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button className={styles.backButton} onClick={handleBackToList}>
            <ArrowLeft size={18} />
            Volver
          </button>
          <div className={styles.detailHeaderInfo}>
            <h2 className={styles.detailTitle}>{selectedGroupDetail.name}</h2>
            {selectedGroupDetail.description && (
              <p className={styles.detailSubtitle}>{selectedGroupDetail.description}</p>
            )}
          </div>
        </div>

        {/* Encabezado compacto con permisos y búsqueda */}
        <div className={styles.compactHeader}>
          {/* Permisos base compactos */}
          <div className={styles.compactPermissions}>
            <span className={styles.compactLabel}>
              <Shield size={14} />
              Permisos del grupo:
            </span>
            <div className={styles.compactPermissionTags}>
              {totalPermissions.length > 0 ? (
                totalPermissions.map(permId => {
                  const perm = availablePermissions.find(p => p.id === permId);
                  const color = getPermissionColor(permId);
                  return (
                    <span
                      key={permId}
                      className={styles.miniPermissionTag}
                      style={{
                        backgroundColor: `${color}15`,
                        borderColor: `${color}40`,
                        color: color
                      }}
                    >
                      {getPermissionIcon(permId)} {perm?.name || permId}
                    </span>
                  );
                })
              ) : (
                <span className={styles.emptyLabel}>Sin permisos base</span>
              )}
            </div>
          </div>

          {/* Barra de búsqueda compacta */}
          <div className={styles.compactSearchBar}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar usuario..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className={styles.compactSearchInput}
            />
            {userSearchTerm && (
              <button
                className={styles.clearSearchBtn}
                onClick={() => setUserSearchTerm('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Lista de usuarios */}
        <div className={styles.detailSection}>
          <div className={styles.usersSectionHeader}>
            <h3 className={styles.sectionTitle}>
              <Users size={16} />
              Usuarios ({filteredUsers.length}/{groupUsers.length})
            </h3>
            <button
              className={styles.addUserButton}
              onClick={handleOpenAddUserModal}
              title="Añadir usuario manualmente"
            >
              <Plus size={16} />
              Añadir usuario
            </button>
          </div>

          {groupUsers.length === 0 ? (
            <div className={styles.emptyUsers}>
              <Users size={32} className={styles.emptyIcon} />
              <p>No hay usuarios en este grupo</p>
              <span className={styles.emptyHint}>
                {selectedGroupDetail.organizationalUnits?.length > 0
                  ? 'No hay usuarios en las OUs seleccionadas'
                  : 'Selecciona OUs para el grupo o asigna permisos custom manualmente'}
              </span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className={styles.emptyUsers}>
              <Search size={32} className={styles.emptyIcon} />
              <p>No se encontraron usuarios</p>
              <span className={styles.emptyHint}>
                Intenta con otro término de búsqueda
              </span>
            </div>
          ) : (
            <div className={styles.usersList}>
            {filteredUsers.map(user => {
              const userCustomData = selectedGroupDetail.customPermissions?.[user.id] || {};
              const userCustomPermissions = typeof userCustomData === 'object' && userCustomData.permissions
                ? userCustomData.permissions
                : (Array.isArray(userCustomData) ? userCustomData : []);

              const userEmailPerms = userCustomData.emailPermissions || { allowedOUs: [] };
              const userClassPerms = userCustomData.classPermissions || { allowedGroups: [] };
              const userReservationPerms = userCustomData.reservationPermissions || { advancedPermissions: [] };

              const expanded = expandedUsers[user.id] || false;

              const hasEmailPerms = userEmailPerms.allowedOUs?.length > 0;
              const hasClassPerms = userClassPerms.allowedGroups?.length > 0;
              const hasReservationPerms = userReservationPerms.advancedPermissions?.length > 0;
              const totalCustom = userCustomPermissions.length +
                                  (hasEmailPerms ? 1 : 0) +
                                  (hasClassPerms ? 1 : 0) +
                                  (hasReservationPerms ? 1 : 0);

              return (
                <div key={user.id} className={styles.userCard}>
                  <div
                    className={styles.userCardHeader}
                    onClick={() => toggleUserExpanded(user.id)}
                  >
                    <div className={styles.userInfo}>
                      <div className={styles.userAvatar}>
                        <UserCog size={14} />
                      </div>
                      <div className={styles.userDetails}>
                        <span className={styles.userName}>
                          {user.firstName} {user.lastName}
                        </span>
                        <span className={styles.userEmail}>{user.id}</span>
                      </div>
                    </div>

                    <div className={styles.userActions}>
                      {totalCustom > 0 && (
                        <span className={styles.customBadge}>
                          <Sparkles size={10} />
                          {totalCustom}
                        </span>
                      )}
                      {/* Botón eliminar solo si está en customPermissions (añadido manualmente desde BD) */}
                      {selectedGroupDetail.customPermissions?.[user.id] && (
                        <button
                          type="button"
                          className={styles.removeUserButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`¿Eliminar a ${user.firstName} ${user.lastName} del grupo?`)) {
                              handleRemoveUserFromGroup(user.id);
                            }
                          }}
                          title="Eliminar del grupo"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <button type="button" className={styles.expandButton}>
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </div>
                  </div>

                  {expanded && (
                    <div className={styles.userCardContent}>
                      {/* Permisos Básicos */}
                      <div className={styles.advancedSection}>
                        <h4 className={styles.advancedSectionTitle}>
                          <Shield size={14} />
                          Permisos Básicos
                        </h4>
                        <p className={styles.helpText}>
                          Permisos adicionales que se suman a los del grupo
                        </p>
                        <div className={styles.permissionGrid}>
                          {availablePermissions.map(permission => {
                            const isSelected = userCustomPermissions.includes(permission.id);
                            const color = getPermissionColor(permission.id);

                            return (
                              <button
                                key={permission.id}
                                type="button"
                                onClick={() => {
                                  const newPermissions = isSelected
                                    ? userCustomPermissions.filter(id => id !== permission.id)
                                    : [...userCustomPermissions, permission.id];
                                  updateUserPermissions(user.id, newPermissions, {
                                    emailPermissions: userEmailPerms,
                                    classPermissions: userClassPerms,
                                    reservationPermissions: userReservationPerms
                                  });
                                }}
                                className={`${styles.permissionButton} ${isSelected ? styles.permissionSelected : ''}`}
                                style={isSelected ? {
                                  backgroundColor: `${color}15`,
                                  borderColor: color,
                                  color: color
                                } : {}}
                              >
                                <span>{getPermissionIcon(permission.id)}</span>
                                <span>{permission.name}</span>
                                {isSelected && <Check size={14} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Permisos de Clases */}
                      {userCustomPermissions.includes('clases') && (
                        <div className={styles.advancedSection}>
                          <h4 className={styles.advancedSectionTitle}>
                            <BookOpen size={14} />
                            Grupos de Clases Permitidos
                          </h4>
                          <div className={styles.compactGrid}>
                            {[
                              { id: 'DBH', name: 'DBH', icon: '📚' },
                              { id: 'LH', name: 'LH', icon: '🎓' },
                              { id: 'Botiquin', name: 'Botiquín', icon: '🏥' }
                            ].map(group => {
                              const isSelected = userClassPerms.allowedGroups?.includes(group.id);
                              return (
                                <button
                                  key={group.id}
                                  type="button"
                                  onClick={() => {
                                    const newGroups = isSelected
                                      ? userClassPerms.allowedGroups.filter(id => id !== group.id)
                                      : [...(userClassPerms.allowedGroups || []), group.id];
                                    updateUserPermissions(user.id, userCustomPermissions, {
                                      emailPermissions: userEmailPerms,
                                      classPermissions: { allowedGroups: newGroups },
                                      reservationPermissions: userReservationPerms
                                    });
                                  }}
                                  className={`${styles.compactButton} ${isSelected ? styles.compactButtonSelected : ''}`}
                                >
                                  <span>{group.icon}</span>
                                  <span>{group.name}</span>
                                  {isSelected && <Check size={12} />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Permisos de Reservas */}
                      {userCustomPermissions.includes('reservas') && (
                        <div className={styles.advancedSection}>
                          <h4 className={styles.advancedSectionTitle}>
                            <Calendar size={14} />
                            Permisos Avanzados de Reservas
                          </h4>
                          <div className={styles.compactGrid}>
                            {[
                              { id: 'manageSpaces', name: 'Gestionar Espacios', icon: <Edit3 size={12} /> },
                              { id: 'createPeriodicReservations', name: 'Reservas Periódicas', icon: <RefreshCw size={12} /> },
                              { id: 'delegateReservations', name: 'Asignar Reservas', icon: <User size={12} /> }
                            ].map(perm => {
                              const isSelected = userReservationPerms.advancedPermissions?.includes(perm.id);
                              return (
                                <button
                                  key={perm.id}
                                  type="button"
                                  onClick={() => {
                                    const newPerms = isSelected
                                      ? userReservationPerms.advancedPermissions.filter(id => id !== perm.id)
                                      : [...(userReservationPerms.advancedPermissions || []), perm.id];
                                    updateUserPermissions(user.id, userCustomPermissions, {
                                      emailPermissions: userEmailPerms,
                                      classPermissions: userClassPerms,
                                      reservationPermissions: { advancedPermissions: newPerms }
                                    });
                                  }}
                                  className={`${styles.compactButton} ${isSelected ? styles.compactButtonSelected : ''}`}
                                >
                                  {perm.icon}
                                  <span>{perm.name}</span>
                                  {isSelected && <Check size={12} />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {viewMode === 'list' ? renderGroupsList() : renderGroupDetail()}

      {/* Modales */}
      {showCreateModal && renderFormModal(false)}
      {showEditModal && renderFormModal(true)}

      {/* Modal para añadir usuario */}
      {showAddUserModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddUserModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Añadir Usuario al Grupo</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowAddUserModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Selecciona usuarios de Firebase para añadirlos manualmente a este grupo.
                Los usuarios añadidos manualmente podrán tener permisos personalizados.
              </p>

              {availableUsersToAdd.length === 0 ? (
                <div className={styles.emptyState}>
                  <Users size={32} />
                  <p>Todos los usuarios ya están en el grupo</p>
                </div>
              ) : (
                <div className={styles.userSelectList}>
                  {availableUsersToAdd.map(user => (
                    <div key={user.id} className={styles.userSelectItem}>
                      <div className={styles.userSelectInfo}>
                        <div className={styles.userSelectAvatar}>
                          <UserCog size={16} />
                        </div>
                        <div className={styles.userSelectDetails}>
                          <span className={styles.userSelectName}>
                            {user.firstName} {user.lastName}
                          </span>
                          <span className={styles.userSelectEmail}>{user.id}</span>
                        </div>
                      </div>
                      <button
                        className={styles.addButton}
                        onClick={() => handleAddUserToGroup(user.id)}
                      >
                        <Plus size={16} />
                        Añadir
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de eliminación */}
      {showDeleteModal && selectedGroup && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.deleteModalContent}>
              <div className={styles.deleteIcon}>
                <AlertCircle size={32} />
              </div>
              <h3 className={styles.deleteTitle}>¿Eliminar Grupo?</h3>
              <p className={styles.deleteText}>
                ¿Estás seguro de eliminar <strong>"{selectedGroup.name}"</strong>?
              </p>
              <div className={styles.warningBox}>
                <Lock size={14} />
                <span>Los usuarios no serán eliminados.</span>
              </div>
            </div>
            <div className={styles.deleteModalFooter}>
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`${styles.button} ${styles.secondaryButton}`}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className={`${styles.button} ${styles.dangerButton}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={16} className={styles.spinning} />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
